'use client';

import type { ReactNode } from 'react';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';

import { LINE, RADIUS, SURFACE } from 'src/theme/ui-tokens';

/**
 * 칩 하나의 치수 — 누르는 칩과 읽기만 하는 칩이 **같은 자를 쓴다**.
 * ⚠️ 높이는 고르개(`HubSelect`)와 **같은 28px** 다 — 한 줄에 섞여 서므로 하나라도 다르면 줄이 울퉁불퉁해진다.
 */
const CHIP_SX = {
  height: 28,
  minWidth: 0,
  px: 1.5,
  borderRadius: RADIUS.pill,
  border: 1,
  typography: 'uiAction',
  whiteSpace: 'nowrap',
} as const;

/**
 * 누르는 조건 칩의 **겉모양 한 벌** — `HubChip` 과 조건 팝오버의 단추(`HubConditionPopover`)가 같이 쓴다.
 * 🚨 단추를 새로 그리는 쪽이 값을 베끼지 마라 — 한 줄에 섞여 서서 하나라도 다르면 줄이 울퉁불퉁해진다.
 * @param active 걸려 있나 — 켜지면 파란 테와 옅은 파란 바탕
 */
export function hubChipSx(active: boolean) {
  return {
    ...CHIP_SX,
    color: active ? 'primary.main' : 'text.secondary',
    bgcolor: active ? 'primary.lighter' : 'background.paper',
    borderColor: active ? 'primary.main' : LINE.edge,
    '& .MuiButton-startIcon': { mr: 0.5, ml: 0 },
    '& .MuiButton-endIcon': { ml: 0.5, mr: 0 },
    '&:hover': { bgcolor: active ? 'primary.lighter' : SURFACE.hover },
  } as const;
}

/**
 * 칩 줄 오른쪽 덩이의 **글자 단추** — 칩과 같은 28px · 알약 · 테두리 없음(채운 단추는 한 줄에 하나다).
 * 🚨 화면마다 값을 베끼지 마라 — 필터 저장·알림 설정·그룹 보기 같은 단추가 한 자로 선다.
 */
export const HUB_QUIET_ACTION_SX = {
  height: 28,
  px: 1.5,
  borderRadius: RADIUS.pill,
  typography: 'uiAction',
  textTransform: 'none',
  whiteSpace: 'nowrap',
  color: 'text.secondary',
} as const;

/**
 * 허브의 조건 칩 — 소재 찾기 툴바와 **같은 크기·같은 결**(28px · `uiAction` · 알약).
 *
 * ⚠️ 화면마다 칩 크기가 다르면 같은 서비스로 안 보인다. 새 칩을 그리지 말고 이것을 쓴다.
 * ⚠️ **막힌 칩(`disabled`)은 자리를 남기고 흐려진다** — 빼 버리면 왜 없는지 알 길이 없다.
 */
export function HubChip({
  label,
  active = false,
  disabled = false,
  onClick,
  icon,
}: {
  label: ReactNode;
  active?: boolean;
  /** 지금 고를 수 없는 칩 — 자리는 남기고 흐려진다 */
  disabled?: boolean;
  onClick: () => void;
  icon?: ReactNode;
}) {
  return (
    <Button
      size="small"
      disabled={disabled}
      onClick={onClick}
      startIcon={icon}
      sx={hubChipSx(active)}
    >
      {label}
    </Button>
  );
}

/**
 * **읽기만 하는 칩** — 지금 걸려 있는 조건을 되보여 준다. 누를 수 없다.
 *
 * 🔑 `HubChip` 을 `disabled` 로 쓰지 않는다 — 흐려진 칩은 「지금은 못 고른다」는 말이라
 *   「이 조건이 걸려 있다」와 뜻이 다르다. 여기는 또렷하게 선다.
 * ⚠️ 누르는 칩과 **같은 자**(`CHIP_SX`)를 쓴다 — 한 줄에 섞여 서기 때문이다.
 * @param active 켜면 누르는 칩의 「걸림」과 같은 파란 테·옅은 파란 바탕
 */
export function HubChipStatic({ label, active = false }: { label: ReactNode; active?: boolean }) {
  return (
    <Box
      sx={{
        ...CHIP_SX,
        display: 'inline-flex',
        alignItems: 'center',
        color: active ? 'primary.main' : 'text.secondary',
        bgcolor: active ? 'primary.lighter' : 'background.paper',
        borderColor: active ? 'primary.main' : LINE.edge,
        borderStyle: 'solid',
      }}
    >
      {label}
    </Box>
  );
}

/** 칩 줄 하나 — 앞에 무엇을 고르는 줄인지 적는다(「기간」·「채널」) */
export function HubChipRow({ label, children }: { label?: string; children: ReactNode }) {
  return (
    <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
      {label && (
        <Typography variant="uiLabel" color="text.disabled" sx={{ mr: 0.25 }}>{label}</Typography>
      )}
      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>{children}</Box>
    </Stack>
  );
}
